import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TopNavigation, {
  TopNavigationProps,
} from "@amzn/awsui-components-react/polaris/top-navigation";
import { Box } from "@amzn/awsui-components-react";
import { useAppDispatch, useAppSelector } from "app/store";
import { i18nStrings, profileActions } from "./config";
import styles from "./style.module.scss";
import logo from "./GaiaLogo.svg";
import { useGetSettingsQuery } from "features/settings/endpoints";
import { useGetTeamsQuery } from "./endpoints";
import { ButtonDropdownProps } from "@amzn/awsui-components-react";
import { GaiaConstants } from "utils";
import { useNavigate } from "react-router-dom";
import {
  setAllTeams,
  setIsUserCriticalInTeam,
} from "features/team-settings/slice";
import { Team } from "models/schemas";
import { useTeamManagement } from "hooks/useTeamManagement";
import { useUIContentLoader } from "hooks/useUIContentLoader";
import { useTeamSettingsLoader } from "hooks/useTeamSettingsLoader";
import { useTeamMembersLoader } from "hooks/useTeamMembersLoader";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import { teamHasSubTeams } from "hooks/useSubTeams";
import { setAssistantOpen } from "features/assistant/slice";
import { useSelectedTeam } from "features/team-settings/hooks";

const Header: React.FC = () => {
  const navigator = useNavigate();
  const { fullName, email } = useAppSelector((state) => state.auth.user);
  const isAssistantOpen = useAppSelector((s) => s.assistant.isOpen);
  const { t } = useTranslation();
  const [userCriticalnessInTeam, setUserCriticalnessInTeam] = useState<{
    [key: string]: boolean;
  }>({});
  const [teamSettingMap, setTeamSettingMap] = useState<Map<string, Team>>(
    new Map(),
  );
  const { data: teamsData } = useGetTeamsQuery();
  const dispatch = useAppDispatch();
  const { isAssistantEnabled } = useSelectedTeam();

  // Get current resource team for centralized data loading
  const { teamName: currentResourceTeam } = useTeamNameSelector();
  useTeamSettingsLoader(currentResourceTeam);
  // Skip member loading for parent teams with sub-teams;
  // forms handle it with the selected sub-team.
  // UI content (country, entity, etc.) is shared across sub-teams, so always load it.
  // Also skip until teamsData is loaded so we know whether the team has sub-teams.
  const memberLoadingTeamName =
    !teamsData?.teams || teamHasSubTeams(teamsData.teams, currentResourceTeam)
      ? ""
      : currentResourceTeam;
  const uiContentLoadingTeamName = !teamsData?.teams ? "" : currentResourceTeam;
  useTeamMembersLoader(memberLoadingTeamName);
  useUIContentLoader(uiContentLoadingTeamName);

  const {
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateHeaderTeamAndDataTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
  } = useTeamManagement();

  useEffect(() => {
    if (teamsData) {
      dispatch(setAllTeams(teamsData));
    }
  }, [teamsData, dispatch]);

  const handleChange = (e: any) => {
    const val = e.detail;
    navigator("/");

    if (val.id !== "guest") {
      updateHeaderTeamAndDataTeam(
        val.id,
        teamsData.teams.find((t: any) => t.teamId === val.id).teamDescription,
      );
      updateUserCriticalnessInTeam(val.id, userCriticalnessInTeam);
      updateTeamDataAndSchemas(val.id, teamSettingMap);
    } else {
      updateSelectedTeam(GaiaConstants.PLACE_HOLDER);
      updateHeaderSelectedTeam("guest", "Guest");
    }
  };

  // Use useEffect for state updates
  useEffect(() => {
    if (!teamsData) return;

    const teamName = localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME);
    const tempUserCriticalness: { [key: string]: boolean } = {};
    const tempTeamSettingMap = new Map<string, Team>();

    // Process regular teams
    teamsData.teams?.forEach((team: Team) => {
      tempUserCriticalness[team.teamId] = team.isCritical;
      tempTeamSettingMap.set(team.teamId, team);
    });

    // Handle initial team selection
    if (!teamName) {
      if (teamsData.teams?.length > 0) {
        const firstTeam = teamsData.teams[0];
        updateHeaderTeamAndDataTeam(
          firstTeam.teamId,
          firstTeam.teamDescription,
        );
        dispatch(setIsUserCriticalInTeam(firstTeam.isCritical));
        updateTeamDataAndSchemas(firstTeam.teamId, tempTeamSettingMap);
      } else if (teamsData.guest) {
        updateSelectedTeam(GaiaConstants.PLACE_HOLDER);
        updateHeaderSelectedTeam("guest", "Guest");
      }
    } else {
      if (teamName === "guest" && teamsData.guest) {
        updateSelectedTeam(GaiaConstants.PLACE_HOLDER); // must remove audit teamName, otherwise this can't be initiated
        updateHeaderSelectedTeam("guest", "Guest");
      } else {
        // Handle existing team selection
        const selectedTeam = teamsData.teams?.find(
          (t: any) => t.teamId === teamName,
        );
        if (selectedTeam) {
          updateSelectedTeam(selectedTeam.teamId);
          updateHeaderTeamAndDataTeam(
            selectedTeam.teamId,
            selectedTeam.teamDescription,
          );
          dispatch(setIsUserCriticalInTeam(selectedTeam.isCritical));
          updateTeamDataAndSchemas(selectedTeam.teamId, tempTeamSettingMap);
        }
      }
    }

    // Store maps for later use
    setUserCriticalnessInTeam(tempUserCriticalness);
    setTeamSettingMap(tempTeamSettingMap);
  }, [teamsData]);

  // Keep getUtilities focused on UI rendering only
  function getUtilities() {
    const utilities: TopNavigationProps.Utility[] = [];
    const teamName = localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME);
    const teams: Array<ButtonDropdownProps.ItemOrGroup> = [];
    let teamDescription = "Choose a team";

    // Build teams dropdown items with hierarchy support
    if (teamsData?.teams) {
      // Build dropdown structure - only include teams without parentTeamResourceId
      teamsData.teams.forEach((team: Team) => {
        // Skip teams that have a parent team
        if (team.parentTeamResourceId) {
          return;
        }

        // If the team has sub-teams, only include it if at least one sub-team is in the teams list
        const childTeamIds = (team as any).subTeams as string[] | undefined;
        if (childTeamIds && childTeamIds.length > 0) {
          const hasChildInList = childTeamIds.some((childId: string) =>
            teamsData.teams.some((t: Team) => t.teamId === childId),
          );
          if (!hasChildInList) {
            return;
          }
        }

        teams.push({
          id: team.teamId,
          text: team.teamDescription,
          iconName: team.teamId === teamName ? "check" : undefined,
        });
      });

      // Add Guest option if the user is a guest to any team resource
      if (teamsData.guest) {
        teams.push({
          id: "guest",
          text: "Guest",
          iconName: teamName === "guest" ? "check" : undefined,
        });
      }

      // Find current team description
      if (teamName === "guest") {
        teamDescription = "Guest";
      } else {
        const currentTeam = teamsData.teams.find(
          (t: any) => t.teamId === teamName,
        );
        if (currentTeam) {
          teamDescription = currentTeam.teamDescription;
        }
      }
    }

    utilities.push({
      type: "menu-dropdown",
      onItemClick: handleChange,
      text: teamDescription,
      items: teams,
    });

    utilities.push({
      type: "menu-dropdown",
      text: fullName,
      description: email,
      iconName: "user-profile",
      items: profileActions,
    });

    if (isAssistantEnabled) {
      utilities.push({
        type: "button",
        variant: "primary-button",
        iconName: "gen-ai",
        text: "Ask Gaia",
        ariaLabel: "Ask Gaia",
        onClick: () => dispatch(setAssistantOpen(!isAssistantOpen)),
      });
    }

    return utilities;
  }

  //get the stage information
  const [stage, setStage] = useState("");
  const { data: settings } = useGetSettingsQuery();

  // Load the is-it-down banner script
  useEffect(() => {
    if (
      (window as any).isItDownScriptLoading ||
      (window as any).isItDownScriptLoaded
    ) {
      return;
    }
    (window as any).isItDownScriptLoading = true;

    // Create and load the script
    const script = document.createElement("script");
    script.src = "https://is-it-down.amazon.com/TAG/components/gaia/stripe.js";
    script.async = true;

    script.onload = () => {
      (window as any).isItDownScriptLoaded = true;
      (window as any).isItDownScriptLoading = false;
    };

    // Add script to head
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (settings?.endpoints?.mainApiUrl) {
      const apiUrl = settings.endpoints.mainApiUrl.toLowerCase();

      if (apiUrl.includes("-alpha") || apiUrl.includes("/alpha")) {
        setStage("TEST ENVIRONMENT - ALPHA");
      } else if (apiUrl.includes("-beta") || apiUrl.includes("/beta")) {
        setStage("TEST ENVIRONMENT - BETA");
      } else if (apiUrl.includes("-gamma") || apiUrl.includes("/gamma")) {
        setStage("TEST ENVIRONMENT - GAMMA");
      } else {
        setStage("");
      }
    }
  }, [settings]);

  return (
    <div id="header" className={styles.header}>
      {stage && (
        <Box className={styles.stageInfo}>
          <span>{stage}</span>
        </Box>
      )}
      <TopNavigation
        i18nStrings={i18nStrings}
        identity={{
          href: "#",
          logo: { src: logo, alt: t("app_name") },
        }}
        utilities={getUtilities()}
      />
    </div>
  );
};

export default Header;
